import type { RouterConfig } from '@nuxt/schema'

  /**
   * 指定されたセレクタの要素が現れるまで待機
   */
  const waitForElement = async (selector: string): Promise<Element | null> => {
    try {
      // 最初にすぐチェック
      let element = document.querySelector(selector);
      if (element) return element;

      // 要素が見つからない場合は最大1秒待機
      for (let i = 0; i < 10; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        element = document.querySelector(selector);
        if (element) return element;
      }

      return null;
    } catch (error) {
      return null;
    }
  };

  /**
   * 画像の読み込み完了を待機（縦長の画像を多用しているため）
   */
  const waitForImages = async (): Promise<void> => {
    const images = document.querySelectorAll('img');
    if (images.length === 0) return;

    const loadPromises = Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();

      return new Promise<void>((resolve) => {
        const cleanup = () => {
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onLoad);
        };

        const onLoad = () => {
          cleanup();
          resolve();
        };

        img.addEventListener('load', onLoad);
        img.addEventListener('error', onLoad);
      });
    });

    await Promise.all(loadPromises);
  };

  /**
   * ハッシュの着地位置。追従ヘッダーに隠れない分だけ下げる。
   *
   * **値は持たず `html { scroll-padding-top }` を読む**（`renewal/vue-adjust.css` の
   * `--sticky-header-h`）。
   *
   * この `scrollBehavior` が横取りするのは **SPA 遷移**（目次クリック・`location.hash` 代入・
   * アプリ内リンク）で、vue-router は要素の絶対座標から `top` を引いて `window.scrollTo` する
   * （`vue-router.mjs:439-451,505-511`）。よって**要素側の `scroll-margin-top` は無視される**。
   * 一方、**初回のフラグメント**（URL 直接アクセス・リロード）は Nuxt が
   * `from === START_LOCATION` のときここを呼ばない（`nuxt/dist/pages/runtime/plugins/router.js:41-44`）
   * ため、ブラウザのネイティブなスクロール＝CSS 側が効く。
   *
   * つまり**経路によって効くものが違う**。ここに固定値を書くと着地位置が経路ごとに変わるので
   * （従来の `top: 80` がその状態だった）、CSS の1箇所を両方が読む形にする。
   * `scroll-padding-top` はブラウザ側のスクロールにも効くので、Ctrl+F・Tab でのフォーカス移動・
   * `scrollIntoView`（`utils/ScrollHelper.ts` の `scrollTargetElement`）も同じ位置に着地する。
   *
   * `window.scrollTo` は座標指定なので `scroll-padding-top` の影響を受けない。
   * **CSS と JS で二重にオフセットが掛かることはない**（全幅で +20px を実測）。
   *
   * `scroll-padding-top` が `auto`（＝未指定）のときは `parseFloat` が `NaN` になるので 0 に倒す。
   */
  const hashScrollOffset = (): number => {
    const padding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop);
    return Number.isNaN(padding) ? 0 : padding;
  };

export default <RouterConfig>{
  async scrollBehavior(to, from, savedPosition) {
    /*
     * ハッシュがある場合（#section など）。
     *
     * **`savedPosition` より先に見る。** 一度訪れたハッシュへの再遷移は vue-router が
     * 履歴移動として扱い `savedPosition` を渡してくるため、後ろに置くと2回目以降の
     * アンカーが「前回そこを離れたときのスクロール位置」に化ける。
     * 連続クリックかどうかは関係なく、間に別のアンカーを挟んでも再現する
     * （`/faq` の目次を #faq-001 → #faq-005 → #faq-001 と押すと3回目が 79px に着地）。
     *
     * 引き換えに、ハッシュ付きURLへ戻る/進むで戻ったときは保存位置ではなくアンカー位置へ飛ぶ。
     * `scrollBehavior` の引数ではアンカークリックと履歴移動を区別できないので両立できず、
     * アンカーが毎回正しく動くほうを採る。ハッシュ無しのページの位置復元は変わらない。
     */
    if (to.hash) {
      await waitForElement(to.hash);
      await waitForImages();
      return {
        el: to.hash,
        behavior: 'smooth',
        top: hashScrollOffset() // 追従ヘッダーの高さ分（`html { scroll-padding-top }` を読む）
      }
    }

    // ブラウザの戻る/進むボタンの場合、保存された位置に戻る
    if (savedPosition) {
      return savedPosition
    }

    // 通常のページ遷移では上部にスムーズスクロール
    return {
      top: 0,
    }
  }
}
